// feat_407 — Configuración de Soporte (SOLO master): IA (tres llaves), respuesta automática,
// umbral y topes, proveedor/modelo/key enmascarada con "Probar", mail de aviso, hechos fijos,
// flag supportEnabled por empresa y permiso "Atiende soporte".
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, FormControlLabel, MenuItem, Paper, Switch,
  TextField, Typography, makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { errorText, fmtDateTime } from "./shared";

const useStyles = makeStyles((theme) => ({
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: theme.spacing(1.5) },
  row: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  list: { maxHeight: 360, overflow: "auto", border: "1px solid #e3e8eb", borderRadius: 6 },
  item: { display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap" },
}));

const NUM_FIELDS = [
  ["confidenceThreshold", "Umbral de confianza (0,5–1)", 0.01],
  ["dailyAutoReplyLimit", "Tope diario de envíos automáticos", 1],
  ["dailySpendLimitUsd", "Tope diario de gasto (US$)", 0.1],
  ["minMinutesBetweenReplies", "Minutos entre respuestas a la misma empresa", 1],
  ["requestsPerCompanyPerHour", "Consultas por empresa por hora", 1],
  ["messagesPerRequestPerHour", "Mensajes por consulta por hora", 1],
];

const ProviderCard = ({ p, active, onSaved }) => {
  const classes = useStyles();
  const [apiKey, setApiKey] = useState("");
  const [pin, setPin] = useState(p.priceInputPerMTokUsd ?? "");
  const [pout, setPout] = useState(p.priceOutputPerMTokUsd ?? "");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { ...extra };
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      if (pin !== "" && pin !== null) body.priceInputPerMTokUsd = Number(pin);
      if (pout !== "" && pout !== null) body.priceOutputPerMTokUsd = Number(pout);
      await api.put(`/superadmin/support/ai/providers/${p.provider}`, body);
      setApiKey("");
      toast.success("Proveedor guardado.");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const probar = async () => {
    setBusy(true);
    setTest(null);
    try {
      const { data } = await api.post(`/superadmin/support/ai/providers/${p.provider}/test`, {});
      setTest(data);
      onSaved();
    } catch (err) {
      setTest({ ok: false, error: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box style={{ border: `1px solid ${active ? "#00a884" : "#e3e8eb"}`, borderRadius: 6, padding: 12 }} data-provider={p.provider}>
      <div className={classes.row}>
        <Typography variant="subtitle2" style={{ textTransform: "capitalize" }}>{p.provider}</Typography>
        {active && <Chip size="small" label="Activo" style={{ background: "#00a884", color: "#fff" }} />}
        <FormControlLabel control={<Switch checked={!!p.enabled} disabled={busy} onChange={(e) => save({ enabled: e.target.checked })} color="primary" />} label="Habilitado" />
      </div>
      <Typography variant="caption" component="div">Key: {p.hasKey ? <b data-key-masked>{p.keyMasked}</b> : "sin cargar"}</Typography>
      <Box className={classes.row} style={{ marginTop: 6 }}>
        <TextField size="small" variant="outlined" type="password" label={p.hasKey ? "Reemplazar key" : "Cargar key"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="new-password" style={{ flex: 1, minWidth: 180 }} />
        <TextField size="small" variant="outlined" type="number" label="US$/M entrada" value={pin} onChange={(e) => setPin(e.target.value)} style={{ width: 130 }} />
        <TextField size="small" variant="outlined" type="number" label="US$/M salida" value={pout} onChange={(e) => setPout(e.target.value)} style={{ width: 130 }} />
      </Box>
      <Box className={classes.row} style={{ marginTop: 6 }}>
        <Button size="small" variant="contained" color="primary" disabled={busy} onClick={() => save()}>Guardar</Button>
        <Button size="small" variant="outlined" disabled={busy || !p.hasKey} onClick={probar} data-provider-test>Probar</Button>
        {p.hasKey && <Button size="small" disabled={busy} onClick={() => window.confirm("¿Borrar la key?") && save({ clearKey: true })}>Borrar key</Button>}
        {test ? (
          <Typography variant="caption" style={{ color: test.ok ? "#2e7d32" : "#d32f2f" }}>
            {test.ok ? `OK · ${test.model} · ${test.tokensIn}/${test.tokensOut} tokens` : `Falló: ${test.error || test.code}`}
          </Typography>
        ) : p.lastTestAt ? (
          <Typography variant="caption" color="textSecondary">Última prueba {fmtDateTime(p.lastTestAt)}: {p.lastTestOk ? "OK" : `falló (${p.lastTestError || "—"})`}</Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const ConfigTab = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [coSearch, setCoSearch] = useState("");
  const [staff, setStaff] = useState([]);
  const [mail, setMail] = useState(null);
  const [mailKey, setMailKey] = useState("");
  const [mailFrom, setMailFrom] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: cfg }, { data: co }, { data: st }, { data: ml }] = await Promise.all([
        api.get("/superadmin/support/ai/config"),
        api.get("/superadmin/support/company-flags"),
        api.get("/superadmin/support/staff"),
        api.get("/superadmin/support/ai/client-email"),
      ]);
      setMail(ml);
      setMailFrom(ml.from || "");
      setData(cfg);
      setForm(cfg.config);
      setCompanies(co.companies || []);
      setStaff(st.users || []);
    } catch (err) {
      toast.error(errorText(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/superadmin/support/ai/config", patch);
      setData((prev) => ({ ...prev, config: d.config, state: d.state }));
      setForm(d.config);
      toast.success("Configuración guardada.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleCompany = async (c, enabled) => {
    if (enabled && !window.confirm(`¿Prender Soporte para #${c.id} ${c.name}? Sus admins van a ver la sección Soporte.`)) return;
    try {
      await api.put(`/superadmin/support/company-flags/${c.id}`, { enabled });
      setCompanies((list) => list.map((x) => (x.id === c.id ? { ...x, supportEnabled: enabled } : x)));
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  const toggleCompanyMail = async (c, clientEmail) => {
    if (clientEmail && !window.confirm(`¿Prender el aviso por mail al cliente para #${c.id} ${c.name}? Recibirán "Respondimos tu consulta #N" (sin el texto de la respuesta). Solo sale si el envío general también está prendido.`)) return;
    try {
      await api.put(`/superadmin/support/company-flags/${c.id}`, { clientEmail });
      setCompanies((list) => list.map((x) => (x.id === c.id ? { ...x, clientEmail } : x)));
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  const saveMail = async (body) => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/superadmin/support/ai/client-email", body);
      setMail(d);
      setMailFrom(d.from || "");
      setMailKey("");
      toast.success("Guardado.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const togglePerm = async (u, enabled) => {
    try {
      await api.put(`/superadmin/support/staff/${u.id}`, { enabled });
      setStaff((list) => list.map((x) => (x.id === u.id ? { ...x, attendsSupport: enabled } : x)));
      toast.success(enabled ? "Permiso otorgado." : "Permiso quitado.");
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  if (!data || !form) return <CircularProgress />;
  const st = data.state || {};
  const numPatch = () => {
    const patch = {};
    NUM_FIELDS.forEach(([k]) => { if (form[k] !== data.config[k]) patch[k] = Number(form[k]); });
    ["noticeEmail", "fixedFacts", "aiModel", "aiProvider"].forEach((k) => { if (form[k] !== data.config[k]) patch[k] = form[k]; });
    return patch;
  };
  const filteredCo = companies.filter((c) => !coSearch || `${c.id} ${c.name} ${c.brand || ""}`.toLowerCase().includes(coSearch.toLowerCase()));
  const provider = (data.providers || []).find((p) => p.provider === form.aiProvider);

  return (
    <Box>
      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6">IA de Soporte</Typography>
        <Typography variant="body2" style={{ color: st.active ? "#2e7d32" : "#d32f2f", marginBottom: 8 }} data-ai-state={st.active ? "on" : "off"}>
          {st.active ? `IA activa: ${st.provider} · ${st.model}` : `IA apagada (${st.reason || "—"}). Las consultas quedan para atender a mano.`}
          {" · "}gasto de hoy US$ {Number(data.spentTodayUsd || 0).toFixed(4)} de {form.dailySpendLimitUsd}
        </Typography>
        <div className={classes.row}>
          <FormControlLabel control={<Switch checked={!!form.aiEnabled} disabled={busy} onChange={(e) => saveConfig({ aiEnabled: e.target.checked })} color="primary" />} label="AI_SUPPORT_ENABLED (interruptor general)" />
          <FormControlLabel control={<Switch checked={!!form.consoleAiEnabled} disabled={busy} onChange={(e) => saveConfig({ consoleAiEnabled: e.target.checked })} color="primary" />} label="IA de la consola" />
          <FormControlLabel
            control={<Switch checked={!!form.autoReplyEnabled} disabled={busy} onChange={(e) => (!e.target.checked || window.confirm("Con la respuesta automática la IA envía sola cuando la confianza supera el umbral. ¿Prender?")) && saveConfig({ autoReplyEnabled: e.target.checked })} color="primary" />}
            label="Respuesta automática"
          />
        </div>
        <Typography variant="caption" color="textSecondary" component="div">
          Para que la IA trabaje hacen falta las tres llaves: interruptor general, IA de la consola y un proveedor habilitado con key. Con la respuesta automática apagada todo queda en borrador.
        </Typography>
        <Divider style={{ margin: "12px 0" }} />
        <div className={classes.grid}>
          <TextField select size="small" variant="outlined" label="Proveedor" value={form.aiProvider} onChange={(e) => {
            const p = (data.providers || []).find((x) => x.provider === e.target.value);
            setForm({ ...form, aiProvider: e.target.value, aiModel: p?.defaultModel || form.aiModel });
          }}>
            {(data.providers || []).map((p) => <MenuItem key={p.provider} value={p.provider}>{p.provider}</MenuItem>)}
          </TextField>
          <TextField select size="small" variant="outlined" label="Modelo" value={form.aiModel} onChange={(e) => setForm({ ...form, aiModel: e.target.value })}>
            {[...(provider?.suggestedModels || []).map((m) => m.model), ...(provider?.suggestedModels || []).some((m) => m.model === form.aiModel) ? [] : [form.aiModel]].map((m) => (
              <MenuItem key={m} value={m}>{(provider?.suggestedModels || []).find((x) => x.model === m)?.label || m}</MenuItem>
            ))}
          </TextField>
          {NUM_FIELDS.map(([k, label, step]) => (
            <TextField key={k} size="small" variant="outlined" type="number" label={label} value={form[k]} inputProps={{ step }} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          ))}
          <TextField size="small" variant="outlined" label="Mail de aviso al equipo" value={form.noticeEmail} onChange={(e) => setForm({ ...form, noticeEmail: e.target.value })} />
        </div>
        <TextField
          fullWidth multiline minRows={3} variant="outlined" margin="normal" label="Hechos fijos (la IA los toma como ciertos)"
          value={form.fixedFacts || ""} onChange={(e) => setForm({ ...form, fixedFacts: e.target.value })} inputProps={{ maxLength: 4000 }}
          helperText="Uno por línea. No pongas datos de clientes."
        />
        <Button variant="contained" color="primary" disabled={busy || Object.keys(numPatch()).length === 0} onClick={() => saveConfig(numPatch())} data-config-save>
          Guardar cambios
        </Button>
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 6 }}>
          Mail al cliente: apagado (las respuestas se ven dentro de la plataforma, en Soporte). Manuales indexados: {data.knowledge?.sections ?? "—"} secciones.
        </Typography>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6">Proveedores de IA</Typography>
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
          La key se guarda cifrada y nunca se vuelve a mostrar completa.
        </Typography>
        <div className={classes.grid}>
          {(data.providers || []).map((p) => <ProviderCard key={`${p.provider}-${p.keyMasked}-${p.enabled}`} p={p} active={p.provider === data.config.aiProvider} onSaved={load} />)}
        </div>
      </Paper>

      <Paper className={classes.paper} variant="outlined" data-client-email-card>
        <Typography variant="h6">Mail al cliente (Resend)</Typography>
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
          Apagado de fábrica: hoy las respuestas se leen solo en la plataforma. Para prenderlo hacen falta TRES cosas: la key y el remitente de acá, el interruptor general y el interruptor de cada empresa (en «Soporte por empresa»). El mail dice «Respondimos tu consulta #N» con un botón; no incluye el texto de la respuesta.
        </Typography>
        <FormControlLabel
          control={<Switch checked={!!form.clientEmailEnabled} disabled={busy} color="primary" onChange={(e) => {
            if (e.target.checked && !window.confirm("¿Prender el envío general de mail al cliente? Solo sale para las empresas que además tengan el interruptor de mail prendido.")) return;
            saveConfig({ clientEmailEnabled: e.target.checked });
          }} />}
          label={form.clientEmailEnabled ? "Envío general PRENDIDO" : "Envío general apagado"}
        />
        <Typography variant="caption" component="div">Key de Resend: {mail && mail.keySet ? <b data-key-masked>{mail.keyMasked}</b> : "sin cargar"}</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 8 }}>
          <TextField size="small" variant="outlined" type="password" autoComplete="new-password" label="Key de Resend (re_…)" value={mailKey} onChange={(e) => setMailKey(e.target.value)} />
          <TextField size="small" variant="outlined" label='Remitente (ej: Soporte WAIA <soporte@dominio>)' value={mailFrom} onChange={(e) => setMailFrom(e.target.value)} style={{ minWidth: 320 }} />
          <Button variant="contained" color="primary" size="small" disabled={busy} onClick={() => {
            const body = {};
            if (mailKey.trim()) body.apiKey = mailKey.trim();
            if (mailFrom !== (mail && mail.from)) body.from = mailFrom;
            if (!Object.keys(body).length) return toast.info("No hay cambios.");
            saveMail(body);
          }}>Guardar</Button>
          {mail && mail.keySet && <Button size="small" disabled={busy} onClick={() => window.confirm("¿Borrar la key de Resend?") && saveMail({ clearKey: true })}>Borrar key</Button>}
        </Box>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6">Soporte por empresa</Typography>
        <Typography variant="caption" color="textSecondary" component="div">
          Prendido = los admins de esa empresa ven la sección Soporte y el botón en el chat. {companies.filter((c) => c.supportEnabled).length} de {companies.length} prendidas.
        </Typography>
        <TextField size="small" variant="outlined" label="Buscar empresa" value={coSearch} onChange={(e) => setCoSearch(e.target.value)} style={{ margin: "8px 0", minWidth: 240 }} />
        <div className={classes.list}>
          {filteredCo.map((c) => (
            <div key={c.id} className={classes.item} data-company-flag={c.id}>
              <Switch size="small" checked={c.supportEnabled} onChange={(e) => toggleCompany(c, e.target.checked)} color="primary" />
              <Typography variant="body2">#{c.id} {c.name}</Typography>
              <FormControlLabel style={{ marginLeft: 8 }} control={<Switch size="small" checked={!!c.clientEmail} onChange={(e) => toggleCompanyMail(c, e.target.checked)} />} label={<Typography variant="caption">Mail al cliente</Typography>} />
              {c.brand && <Chip size="small" variant="outlined" label={c.brand} />}
              {c.active === false && <Chip size="small" label="inactiva" />}
            </div>
          ))}
        </div>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6">Quién atiende soporte</Typography>
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
          Los superadmins sin marca (master) atienden todo. A un superadmin con marca se le da el permiso y ve SOLO las consultas de las empresas de su marca.
        </Typography>
        <div className={classes.list}>
          {staff.map((u) => (
            <div key={u.id} className={classes.item} data-staff-user={u.id}>
              <Switch size="small" checked={u.attendsSupport} disabled={u.master} onChange={(e) => togglePerm(u, e.target.checked)} color="primary" />
              <Typography variant="body2">{u.name}</Typography>
              <Typography variant="caption" color="textSecondary">{u.email}</Typography>
              <Chip size="small" variant="outlined" label={u.master ? "Master (todas las empresas)" : `Marca: ${u.brand || u.scopeId}`} />
            </div>
          ))}
        </div>
      </Paper>
    </Box>
  );
};

export default ConfigTab;
